import React, { useEffect, useState } from "react";
import { Form, Card, Row, Col, Badge, ProgressBar, Container } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import { useParams } from "react-router-dom";
import { FaBox, FaBoxOpen, FaExclamationTriangle } from "react-icons/fa";
import { eiwrsSiteInventoryServices } from "../../../APIServices/EIWRS/eiwrsSiteInventoryServices";
import { NoDataFoundTable, PageLoader } from "../../../Components/NoDataFoundTable/NoDataFoundTable";
import UseToggle from "../../../Hooks/UseToggle";
import Pagination from "../../Pagination";
import ResupplySite from "./ResupplySite";
import { usersOnboardingService } from "../../../APIServices/usersOnboarding";
import SiteDetail from "./SiteDetail";
import UpdateSiteThreshold from "./UpdateSiteThreshold";
import { FaRegBuilding } from "react-icons/fa6";
import { handleResponse } from "../../../APIHelpers/Responses";

const SiteInventoryDataList = () => {
  const [searchData, setSearchData] = useState("");
  const [rowData, setRowData] = useState("");
  const [siteRows, setSiteRows] = useState([]);
  const [siteRowsCount, setSiteRowsCount] = useState(0);
  const [totalStockKit, setTotalStockKit] = useState(0);
  const [totalDispenseKit, setTotalDispenseKit] = useState(0);
  const [userList, setUserList] = useState([]);
  const {
    toggle, setToggle,
    toggle1, setToggle1,
    toggle2, setToggle2,
  } = UseToggle();
  const [loading, setLoading] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);
  let { studyId } = useParams();

  const StudyMode = JSON.parse(localStorage.getItem("StudyMode"));
  const eiwrsPermission = JSON.parse(localStorage.getItem("studyPermissionData"));

  const getAssignedAppUser = async () => {
    let data = await usersOnboardingService.getAssignedAppUser(studyId, "edc");
    setUserList(data?.data || []);
  };

  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const startIndex = (pageNumber - 1) * itemsPerPage;
  const totalPage = Math.ceil(siteRowsCount / itemsPerPage);

  const getStudyEIWRSSiteInventory = async (e) => {
    setLoading(true);
    let data = await eiwrsSiteInventoryServices.getStudyEIWRSSiteInventory(
      studyId,
      StudyMode,
      searchData,
      pageNumber,
      itemsPerPage
    );
    setSiteRows(data?.data || []);
    setSiteRowsCount(data?.count || 0);
    setTotalStockKit(data?.countKitStock || 0);
    setTotalDispenseKit(data?.countDispenseKit || 0);
    setRowData("");
    setLoading(false);
  };

  useEffect(() => {
    getAssignedAppUser();
  }, []);

  useEffect(() => {
    getStudyEIWRSSiteInventory();
  }, [searchData, pageNumber, itemsPerPage]);

  const overviewCards = [
    {
      title: "Total Sites",
      value: siteRowsCount,
      subtitle: "Total sites participating in the study",
      icon: <FaRegBuilding className="bg-white text-primary shadow-sm" />,
    },
    {
      title: "Dispensed",
      value: totalDispenseKit,
      subtitle: "Total kits dispensed to study subjects",
      icon: <FaBoxOpen className="bg-white text-danger shadow-sm" />,
    },
    {
      title: "Total Kit Stock",
      value: totalStockKit,
      subtitle: "Total kits currently available across all sites",
      icon: <FaBox className="bg-white text-success shadow-sm" />,
    }
  ];

  const downloadSiteKitReport = async (siteId) => {
    setDownloadLoader(true)
    let data = await eiwrsSiteInventoryServices.downloadSiteKitReport(studyId, siteId, StudyMode);
    downloadCSV(data?.data || []);
    setDownloadLoader(false)
  };

  const downloadCSV = (data) => {
    if (data.length === 0) {
      handleResponse({
        statusCode: 400,
        customMessage: "Kit not found"
      })
    }
    if (!data || !data.length) return;
    const headers = [
      "Sr.",
      "Site",
      "Kit Number",
      "Kit Type",
      "Batch Number",
      "Treatment",
      "Expiry DT",
      "Status",
    ];
    const rows = data.map((kitData, index) => [
      index + 1,
      kitData.site,
      kitData.kitNumber,
      kitData.kitType,
      kitData.batchNumber,
      (eiwrsPermission?.liveTreatmentUnblinded || eiwrsPermission?.qcTreatmentUnblinded || eiwrsPermission?.dbpTreatmentUnblinded) ? kitData.treatment || "_" : "Blinded",
      kitData.expiryDate,
      kitData?.quarantined ? "quarantined" : kitData?.status === "inPreparation" ? "In Preparation" : kitData?.status === "pendingQC" ? "Pending QC" : kitData?.status === "failedQC" ? "Failed QC" : kitData?.status === "available" ? "Available at Depot" : kitData?.status === "allocated" ? "Allocated at Site" : kitData?.status === "passedQC" ? "Passed QC" : kitData?.status,
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(val => `"${val}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "Site_Kit_Inventory.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="overflow-auto" style={{ height: "calc(100vh - 145px)" }}>
        {siteRowsCount > 0 && (
          <div className="justify-content-between toolbar border-bottom hstack p-1" style={{ height: 36 }}>
            <div className="position-relative">
              <Form.Control
                type="Search"
                onChange={(e) => setSearchData(e.target.value)}
                value={searchData}
                placeholder="Search..."
                style={{ width: 200, padding: "3px 10px" }}
              />
              <CiSearch
                className="CP position-absolute bg-white"
                title="Search"
                style={{ width: 15, top: 4, right: 7 }}
              />
            </div>
            <button
              className="updateProfile"
              style={{padding: "1px 10px"}}
              disabled={downloadLoader}
              onClick={() => downloadSiteKitReport("all")}
            >
              Download CSV
            </button>
          </div>
        )}
        <Container fluid>
          <Row className="g-2">
            {overviewCards?.map((item, index) => (
              <Col xl={4} lg={4} md={6} sm={12} key={index} className="pt-2">
                <Card className="app-card bg-light border h-100">
                  <Card.Body>
                    <div className="d-flex gap-3">
                      <div className="study_icopn">
                        {item.icon}
                      </div>
                      <div>
                        <Card.Title style={{ fontSize: 16 }}>{item.title}</Card.Title>
                        <Card.Text className="m-0">{item.value || "0"}</Card.Text>
                        <Card.Text style={{ fontSize: 11 }}>{item.subtitle}</Card.Text>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
        {loading ? (
          <PageLoader />
        ) : siteRowsCount === 0 ? (
          <NoDataFoundTable MSG={"Site Data not found!"} />
        ) : siteRows?.length === 0 ? (
          <NoDataFoundTable MSG={"Search Site Data not found!"} />
        ) : (
          <>
            <Container fluid>
              <Row className="g-2 py-2">
                {siteRows?.map((row, index) => (
                  <Col key={row?.id || index} xs={12} md={4} lg={4} xl={4}>
                    <Card className="h-100 app-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1 fw-bold">
                              Site {row?.row?.SITEID}
                            </h6>
                            <span className="text-muted small">{row?.row?.SITENAME}</span>
                          </div>
                          <Badge
                            bg="success-subtle"
                            style={{padding: "1px 10px"}}
                            className={`br3 text-success px-2 py-1 text-capitalize`}
                          >
                            Active
                          </Badge>
                        </div>
                        <div className="mb-2">
                          <div className="d-flex justify-content-between small">
                            <span className="text-muted">
                              PI: {userList
                                ?.filter((user) => (row?.row?.PI || [])?.includes(user?.userId))
                                ?.map((user) => user?.fullName)
                                ?.join(", ") || "Not Assign"}
                            </span>
                          </div>
                        </div>
                        <hr className="my-2" />
                        <div className="mb-2">
                          <div className="d-flex justify-content-between small">
                            <div className="hstack gap-2">
                              <span className="text-muted">Stock Level</span>
                              {row?.kitProgress >= Number(row?.row?.[StudyMode === "DBP Mode" ? "EIWRSDBPKITTHRESHOLD" : StudyMode === "Live Mode" ? "EIWRSLIVEKITTHRESHOLD" : "EIWRSQCKITTHRESHOLD"]) &&
                                <span style={{ fontSize: 12 }} className="hstack gap-1 text-danger small mt-1">
                                  <FaExclamationTriangle size={10} className="me-1" />
                                  Below resupply threshold
                                </span>
                              }
                              {row?.expiredKit &&
                                <span style={{ fontSize: 12 }} className="hstack gap-1 text-warning small mt-1">
                                  <FaExclamationTriangle size={10} className="me-1" />
                                  Some kits expiring soon (Within 7 days)
                                </span>
                              }
                              {row?.kitProgress > 90 &&
                                <span style={{ fontSize: 10 }} className="hstack gap-1 text-danger">
                                  <FaExclamationTriangle size={10} className="me-1" />
                                  {row?.kitProgress === 100 ? "Finished Stock" : "Low Stock"}
                                </span>
                              }
                            </div>
                            <span className="text-muted small fw-bold">
                              {row?.usedKit} / {row?.totalKit} max
                              <span className="text-muted fw-normal ms-1">· threshold {row?.row?.[StudyMode === "DBP Mode" ? "EIWRSDBPKITTHRESHOLD" : StudyMode === "Live Mode" ? "EIWRSLIVEKITTHRESHOLD" : "EIWRSQCKITTHRESHOLD"]}</span>
                            </span>
                          </div>
                          <ProgressBar
                            now={row?.kitProgress}
                            variant={rowData?.kitProgress > 90 ? "danger" : rowData?.kitProgress > 80 ? "warning" : "success"}
                            style={{ height: 5 }}
                            className="mt-1"
                          />
                        </div>
                        <Row className="g-1 mb-2">
                          <Col xs={3}>
                            <div className="text-center p-1 bg-light rounded">
                              <div className="small text-muted">Total</div>
                              <Form.Label className="m-0">{row?.totalKit}</Form.Label>
                            </div>
                          </Col>
                          <Col xs={3}>
                            <div className="text-center p-1 bg-success bg-opacity-10 rounded">
                              <div className="small text-success" style={{fontSize: 11}}>Available</div>
                              <Form.Label className="m-0">{row?.totalKit - row?.usedKit}</Form.Label>
                            </div>
                          </Col>
                          <Col xs={3}>
                            <div className="text-center p-1 bg-info bg-opacity-10 rounded">
                              <div className="small text-info" style={{fontSize: 11}}>Dispensed</div>
                              <Form.Label className="m-0">{row?.usedKit}</Form.Label>
                            </div>
                          </Col>
                          <Col xs={3}>
                            <div className="text-center p-1 bg-danger bg-opacity-10 rounded">
                              <div className="small text-danger" style={{fontSize: 11}}>Quarantine</div>
                              <Form.Label className="m-0">{row?.totalQuarantinedKit}</Form.Label>
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex justify-content-between small border-top pt-2">
                          <div>
                            <span className="text-muted">Subjects</span>
                            <div className="text-center">{row?.totalSubject}</div>
                          </div>
                          <div>
                            <span className="text-muted">Randomized</span>
                            <div className="text-center">{row?.totalRandomizedSubject}</div>
                          </div>
                          <div>
                            <span className="text-muted">Dispensed</span>
                            <div className="text-center">{row?.usedKit}</div>
                          </div>
                        </div>
                      </Card.Body>
                      <Card.Footer className="bg-white border-top hstack justify-content-end gap-2">
                        <button
                          className="updateProfile"
                          style={{padding: "1px 10px"}}
                          disabled={downloadLoader}
                          onClick={() => downloadSiteKitReport(row?.siteId)}
                        >
                          {/* <FaDownload className="me-1" size={12} /> */}
                          Download CSV
                        </button>
                        {(StudyMode === "DBP Mode" || eiwrsPermission?.liveSiteManagement || eiwrsPermission?.qcSiteManagement) &&
                          <button
                            className="updateProfile"
                            style={{padding: "1px 10px"}}
                            onClick={() => {
                              setRowData(row);
                              setToggle2();
                            }}
                          >
                            {/* <FaEdit className="me-1" size={12} /> */}
                            Update Threshold
                          </button>
                        }
                        <button
                          className="updateProfile"
                          style={{padding: "1px 10px"}}
                          onClick={() => {
                            setRowData(row);
                            setToggle();
                          }}
                        >
                          {/* <FaEye className="me-1" size={12} /> */}
                          View
                        </button>
                        {(StudyMode === "DBP Mode" || eiwrsPermission?.liveKitShipmentManagement || eiwrsPermission?.qcKitShipmentManagement) &&
                          <button
                            className="removeProfile"
                            style={{padding: "1px 10px"}}
                            onClick={() => {
                              setRowData(row);
                              setToggle1();
                            }}
                          >
                            {/* <FaTruck className="me-1" size={12} /> */}
                            Resupply
                          </button>
                        }
                      </Card.Footer>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Container>

            <div className="position-sticky bottom-0 start-0">
              <Pagination
                totalPage={totalPage}
                pageNumber={pageNumber}
                itemsPerPage={itemsPerPage}
                totalItems={siteRowsCount}
                setPageNumber={setPageNumber}
                setItemsPerPage={setItemsPerPage}
              />
            </div>
          </>
        )}
      </div>
      <SiteDetail
        Show={!toggle}
        Hide={setToggle}
        rowData={rowData}
        Title={`View ${rowData?.row?.SITENAME} Details`}
        eiwrsPermission={eiwrsPermission}
      />
      <ResupplySite
        Show={!toggle1}
        Hide={setToggle1}
        Title={`Resupply — Site ${rowData?.row?.SITEID || ""}`}
        rowData={rowData}
        getStudyEIWRSSiteInventory={getStudyEIWRSSiteInventory}
      />
      <UpdateSiteThreshold
        Show={!toggle2}
        Hide={setToggle2}
        Title={`Update — Site ${rowData?.row?.SITEID || ""} Threshold`}
        rowData={rowData}
        getStudyEIWRSSiteInventory={getStudyEIWRSSiteInventory}
      />
    </>
  );
};

export default SiteInventoryDataList;